import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { ToolsService } from 'src/app/api/tools.service';
import { CommonModule } from '@angular/common';
import { UiModule } from 'src/app/ui/ui.module';
import { MessageService } from 'primeng/api';
import { TripLeaderStaffingDetails, SiteLeaderDetailsRequest, AssistantLeaderRequest } from 'src/app/api';
import { Column } from 'src/app/ui';
import { TableCaptionComponent } from "../../../ui/tables/table-caption/table-caption.component";
import { TableColumnsComponent } from "../../../ui/tables/table-columns/table-columns.component";
import { DateRangePickerComponent, DateRangePresets } from "../../../ui/date-range-picker/date-range-picker.component";
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { TableHeadersComponent } from 'src/app/ui/tables';
import { DateValidators } from 'src/app/ui/validators/dateValidators';
import { CheckboxModule } from 'primeng/checkbox';

@Component({
  selector: 'app-seasonal-staffing',
  imports: [CheckboxModule, CommonModule, UiModule, TableCaptionComponent, TableColumnsComponent, DateRangePickerComponent, ReactiveFormsModule, TableHeadersComponent],
  templateUrl: './seasonal-staffing.component.html',
  styleUrl: './seasonal-staffing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,

})
export class SeasonalStaffingComponent {

  searchForm = this.fb.group({
    dateRange: this.fb.control<Date[]>(this.initialDateRange(), [DateValidators.dateRangeRequired]),
    useEndDate: this.fb.control<boolean>(false)

  });

  constructor(
    private toolsService: ToolsService,
    private messageService: MessageService,
    private fb: FormBuilder
  ) {

  }

  initialDateRange(): Date[] {

    if (DateTime.now().day < 15) {
      return [
        DateTime.now().minus({months: 1}).set({day: 16}).toJSDate(),
        DateTime.now().minus({months: 1}).endOf('month').toJSDate()
      ]
    }
    else {
      return [
        DateTime.now().set({ day: 1 }).toJSDate(),
        DateTime.now().set({day: 15}).toJSDate()
      ]
    }

  }

  tripLeaderStaffing = signal<TripLeaderStaffingDetails[]>([]);
  siteLeaderDetails = signal<SiteLeaderDetailsRequest[]>([]);
  assistantLeaderDetails = signal<AssistantLeaderRequest[]>([]);

  isLoading = signal(false);
  hasSearched = signal(false);

  tripLeaderColumns = signal<Column<TripLeaderStaffingDetails>[]>([
    { field: 'tripId', header: 'Trip ID', type: 'tripIdWithLink', tripIdField: 'tripId', custIdField: 'custId', sortable: true, filterable: true },
    { field: 'tripdate', header: 'Trip Date', type: 'date', sortable: true, filterable: true },
    { field: 'tripEndDate', header: 'Trip End Date', type: 'date', sortable: true, filterable: true },
    { field: 'locName', header: 'Location', type: 'text', sortable: true, filterable: true },
    { field: 'tl', header: 'Trip Leader Name', type: 'text', sortable: true, filterable: true },
    { field: 'address1', header: 'Address', type: 'text', sortable: true, filterable: true },
  ]);

  siteLeaderDetailsColumns = signal<Column<SiteLeaderDetailsRequest>[]>([
    { field: 'tripId', header: 'Trip ID', type: 'tripIdWithLink', tripIdField: 'tripId', custIdField: 'custId', sortable: true, filterable: true },
    { field: 'tripdate', header: 'Trip Date', type: 'date', sortable: true, filterable: true },
    { field: 'tripEndDate', header: 'Trip End Date', type: 'date', sortable: true, filterable: true },
    { field: 'locName', header: 'Location', type: 'text', sortable: true, filterable: true },
    { field: 'sl', header: 'Site Leader Name', type: 'text', sortable: true, filterable: true },
    { field: 'address1', header: 'Address', type: 'text', sortable: true, filterable: true },
  ])

  assistantLeaderDetailsColumns = signal<Column<AssistantLeaderRequest>[]>([
    { field: 'tripId', header: 'Trip ID', type: 'tripIdWithLink', tripIdField: 'tripId', custIdField: 'custId', sortable: true, filterable: true },
    { field: 'tripdate', header: 'Trip Date', type: 'date', sortable: true, filterable: true },
    { field: 'tripEndDate', header: 'Trip End Date', type: 'date', sortable: true, filterable: true },
    { field: 'locName', header: 'Location', type: 'text', sortable: true, filterable: true },
    { field: 'assistant', header: 'Assistant Leader Name', type: 'text', sortable: true, filterable: true },
    { field: 'address1', header: 'Address', type: 'text', sortable: true, filterable: true },
  ]);

  tripDatePresets: DateRangePresets[] = [
    { label: "This Year", value: [DateTime.now().startOf("year").toJSDate(), DateTime.now().endOf("year").toJSDate()] },
    { label: "Last Year", value: [DateTime.now().minus({ years: 1 }).startOf("year").toJSDate(), DateTime.now().minus({ years: 1 }).endOf("year").toJSDate()] },
    { label: "Payroll 1-15", value: [DateTime.now().set({day: 1}).toJSDate(), DateTime.now().set({day: 15}).toJSDate()] },
    { label: "Payroll 16-end (this month)", value: [DateTime.now().set({day: 16}).toJSDate(), DateTime.now().endOf("month").toJSDate()] },
    { label: "Payroll 16-end (last month)", value: [DateTime.now().minus({months: 1}).set({day: 16}).toJSDate(), DateTime.now().minus({months: 1}).endOf("month").toJSDate()] }
  ];

  search(): void {
    if (!this.searchForm.valid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly.'
      });
      return;
    }

    this.isLoading.set(true);
    this.hasSearched.set(true);

    const formValue = this.searchForm.value;
    const startDate = DateTime.fromJSDate(formValue.dateRange![0]).toFormat('yyyy-MM-dd');
    const endDate = DateTime.fromJSDate(formValue.dateRange![1]).toFormat('yyyy-MM-dd');
    const useTripEndDate = formValue.useEndDate ?? false;

    this.toolsService.getSeasonalStaffing(startDate, endDate, useTripEndDate).subscribe({
      next: (data) => {

        this.tripLeaderStaffing.set(data.tripLeaders || []);
        this.siteLeaderDetails.set(data.siteLeaders || []);
        this.assistantLeaderDetails.set(data.assistantLeaders || []);

        this.isLoading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Seasonal staffing data loaded successfully'
        });
      },
      error: (err) => {
        this.isLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load seasonal staffing data'
        });
        console.error('Error loading seasonal staffing data:', err);
      }
    });
  }

}
